// ═══════════════════════════════════════════════════
// AuthGuard — Protects routes behind authentication
// Checks token in localStorage, verifies via /auth/me,
// redirects to /login if no valid session
// ═══════════════════════════════════════════════════

import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { useSession } from '@/hooks/useAuth';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  // If we have a token, verify the session
  const { isLoading, isError } = useSession();

  // No token at all — go to login immediately
  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Token exists but still verifying — show spinner
  if (isLoading && !isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--void)',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid rgba(123, 47, 255, 0.2)',
            borderTopColor: '#7B2FFF',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}>
            Verifying session...
          </span>
        </div>
      </div>
    );
  }

  // Verification failed — go to login
  if (isError && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Authenticated (either from store or from successful /auth/me)
  return <>{children}</>;
}
